import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'react-toastify';
import {sendApiRequest} from '../../utils/api'
import { FiEdit, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';

const TutorialManagement = () => {
  const [tutorials, setTutorials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, setValue } = useForm();

  // Fetch tutorials
  const fetchTutorials = async () => {
    try {
      const response = await sendApiRequest('api/tutorials',{},'GET');
      setTutorials(response.data);
    } catch (error) {
      toast.error('Failed to fetch tutorials');
    }
  };

  useEffect(() => {
    fetchTutorials();
  }, []);

  // Handle form submission
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (editingId) {
        await sendApiRequest(`api/tutorials/${editingId}`, data, 'PUT');
        toast.success('Tutorial updated successfully');
      } else {
        await sendApiRequest('api/tutorials', data, 'POST');
        toast.success('Tutorial created successfully');
      }
      setIsModalOpen(false);
      reset();
      setEditingId(null);
      fetchTutorials();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Something went wrong');
    }
    setIsLoading(false);
  };

  // Handle edit
  const handleEdit = (tutorial) => {
    setValue('title', tutorial.title);
    setValue('videoUrl', tutorial.videoUrl);
    setEditingId(tutorial._id);
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tutorial?')) return;
    
    try {
      await sendApiRequest(`api/tutorials/${id}`, {}, 'DELETE');
      toast.success('Tutorial deleted successfully');
      fetchTutorials();
    } catch (error) {
      toast.error('Failed to delete tutorial');
    }
  };

  // Reset form and close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
    setEditingId(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Tutorial Management</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center"
        >
          <FiPlus className="mr-2" /> Add New Tutorial
        </button>
      </div>

      {/* Tutorials Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Video URL
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tutorials.map((tutorial) => (
              <tr key={tutorial._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {tutorial.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <a 
                    href={tutorial.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {tutorial.videoUrl}
                  </a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(tutorial)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                  >
                    <FiEdit className="inline-block" />
                  </button>
                  <button
                    onClick={() => handleDelete(tutorial._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FiTrash2 className="inline-block" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h2 className="text-lg font-medium">
                {editingId ? 'Edit Tutorial' : 'Add New Tutorial'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="videoUrl">
                  Video URL
                </label>
                <input
                  id="videoUrl"
                  type="url"
                  {...register('videoUrl', { required: 'Video URL is required' })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    'Saving...'
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      {editingId ? 'Update' : 'Save'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialManagement;